import "./App.css";
import { useState } from "react";
import { Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import Home from "./screens/Home/Home";
import StoreProvider from "./store/StoreProvider";
import WarehousesList from "./screens/WarehousesList/WarehousesList";
import Header from "./components/Header/Header";
import WarehouseInventory from "./screens/WarehouseInventory/WarehouseInventory";
import NotFoundPage from "./screens/NotFoundPage/NotFoundPage";

import { jwtDecode } from "jwt-decode";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useContext, useEffect } from "react";
import ElementFinder from "./screens/ElementFinder/ElementFinder";
import useQueryParams from "./hooks/useQueryParams";
import VisualizerDataView from "./screens/VisualizerDataView/VisualizerDataView";
import { Store } from "./store";
import RackFrontView from "./components/RackFrontView/Index";
import { initial } from "lodash";
import { eventEmiterInstance } from "./utils/EventEmitter";
import axios from "./service/axios";
import Loader from "./components/Loader/Loader";

const PrivateRoutes = () => {
  const { state, dispatch } = useContext(Store);

  useEffect(() => {
    window.addEventListener("fullscreenchange", (event) => {
      dispatch({
        type: "SET_FULLSCREEN_MODE_ENABLED",
        payload: !!document.fullscreenElement,
      });
    });
  }, []);

  const token = localStorage.getItem("_stack360_token");

  let auth = { token: token };
  return auth.token ? <Outlet /> : <Navigate to="/forbidden" />;
};

const generateVisualizerAccessToken = async (payload) => {
  const { stackToken, isMultiMode, warehouseCode } = payload;
  const { data } = await axios.get("/auth/generate-visualizer-access-token", {
    params: {
      authToken: stackToken,
      isMultiMode: isMultiMode,
      warehouseCode: warehouseCode,
    },
  });
  return data.accessToken;
};

const Authenticator = () => {
  const { state, dispatch } = useContext(Store);
  const navigate = useNavigate();
  const [authErrorMessage, setAuthErrorMessage] = useState(null);

  useEffect(() => {
    dispatch({ type: "SHOW_GLOBAL_LOADER", payload: { isActive: true, loaderText: "Authenticating, please wait" } });

    const performAuthentication = async () => {
      // get the warehousecode and userId from the query params of url
      try {
        const queryParams = new URLSearchParams(window.location.search);

        const stackToken = queryParams.get("authToken");

        const isMultiMode = queryParams.get("multiMode") || false;

        const warehouseCode = queryParams.get("warehouseCode") || null;

        const binLocation = queryParams.get("binLocation") || null;

        const navigateTo = queryParams.get("navigateTo") || null;

        const payload = { stackToken, isMultiMode, warehouseCode };

        const token = await generateVisualizerAccessToken(payload);

        if (token) {
          const decodedToken = jwtDecode(token);

          localStorage.setItem("_stack360_token", token);
          // localStorage.setItem("organizationType", decodedToken.orgType);
          localStorage.setItem("multiMode", decodedToken.multiMode);

          if (navigateTo === "element-finder") {
            const params = new URLSearchParams();
            params.set("warehouseCode", warehouseCode);
            if (binLocation) {
              params.set("binLocation", binLocation);
            }
            navigate(`/element-finder?${params.toString()}`);
          } else {
            navigate("/");
          }
        }
      } catch (error) {
        setAuthErrorMessage("Failed to authenticate");
        dispatch({ type: "HIDE_GLOBAL_LOADER" });
        console.log("error", error);
        throw error;
      }
    };

    performAuthentication();
  }, []);
  return <div className="authenticating-container">{authErrorMessage && <div>{authErrorMessage}</div>}</div>;
};

const MessageTypes = {
  INIT: "INIT",
  READY: "READY",
  SHOW_LOCATION: "SHOW_LOCATION",
  HIDE_LOCATION: "HIDE_LOCATION",
  ERROR: "ERROR",
};

const AppContent = () => {
  const {
    state: { globalLoader, globalShowLocation },
    dispatch,
  } = useContext(Store);
  const { isActive } = globalLoader;

  const sendMessage = (message) => {
    const referrer = document.referrer;
    const parentOrigin = referrer ? new URL(referrer).origin : "*";
    // Send message to parent (WMS application)
    window.parent.postMessage(message, "*");
  };

  const sendReady = () => {
    sendMessage({
      type: MessageTypes.READY,
    });
  };

  useEffect(() => {
    const handleMessage = (e) => {
      const { data } = e;
      const { type, payload } = data;

      switch (type) {
        case MessageTypes.READY:
          console.log("MessageTypes.READY");
          break;
        case MessageTypes.INIT:
          console.log("MessageTypes.INIT");
          break;
        case MessageTypes.SHOW_LOCATION:
          console.log("MessageTypes.SHOW_LOCATION", payload);
          dispatch({ type: "SET_GLOBAL_SHOW_LOCATION", payload });
          //eventEmiterInstance.emit("global-show-location", payload);
          break;
        case MessageTypes.HIDE_LOCATION:
          console.log("MessageTypes.HIDE_LOCATION", payload);
          eventEmiterInstance.emit("global-hide-location");
          break;
        case MessageTypes.ERROR:
          console.log("MessageTypes.ERROR");
          break;
        default:
        //console.log("Unkown Message Type", type);
      }
    };

    eventEmiterInstance.on("misc/layout-objects-loaded", sendReady);

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      eventEmiterInstance.off("misc/layout-objects-loaded", sendReady);
    };
  }, []);

  return (
    <>
      <Routes>
        <Route path="/authenticate" element={<Authenticator />} />
        <Route element={<PrivateRoutes />}>
          <Route path="" element={<Navigate to="/warehouses" />} />
          <Route path="warehouses" element={<WarehousesList />} />
          <Route path="visualizer-dataview" element={<VisualizerDataView />} />
          <Route path="layout-editor" element={<Home />} />
          <Route path="rack-editor" element={<RackFrontView />} />
          <Route path="warehouse-inventory" element={<WarehouseInventory />} />
          <Route path="element-finder" element={<ElementFinder />} />
        </Route>
        <Route path="forbidden" element={<Header title={"Forbidden"} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {isActive && <Loader />}
      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover={false}
        theme="colored"
        closeButton={false}
      />
    </>
  );
};

const App = () => {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  );
};

export default App;
